import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import '@/engine/store';
import '@/engine/global-logic';
import './index.css';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { ModalProvider, ThemeProvider } from 'ht-sdk/react';

// Import the generated route tree
import { routeTree } from './routeTree.gen';
import { Toaster } from 'sonner';
import { theme } from '@/theme';
import { init as SentryInit, browserTracingIntegration, replayIntegration, captureMessage as SentryCaptureMessage } from '@sentry/browser';

const consoleError = console.error;
console.error = (msg, ...data) => {
	SentryCaptureMessage([msg, ...data].join(' '), 'error');
	consoleError(msg, ...data);
};

SentryInit({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	integrations: [browserTracingIntegration(), replayIntegration()],
	// Tracing
	tracesSampleRate: 1.0, //  Capture 100% of the transactions
	// Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
	tracePropagationTargets: [], // ['/api'],
	// Session Replay
	replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
	replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Create a new router instance
export const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router;
	}
}

// Render the app
const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
	const root = ReactDOM.createRoot(rootElement);
	root.render(
		<StrictMode>
			<ThemeProvider value={theme}>
				<ModalProvider>
					<RouterProvider router={router} />
					<Toaster />
				</ModalProvider>
			</ThemeProvider>
		</StrictMode>,
	);
}
